import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { RiRepeatLine } from 'react-icons/ri';
import { MdOutlineDelete, MdOutlineMoreHoriz } from 'react-icons/md';
import { Chip, TmdxAvatar, Tooltip } from '@ds';
import { ActionMenu, ConditionalRender, MenuItemType, TableColumnType } from '@ui';
import { CustomerMT } from '@/api/apis/customer';
import { getFullName } from '@/utils/helpers';
import { CUSTOMER_PERMISSION_NAMES, CUSTOMER_ROLE_ADMIN_NAMES } from '@/constants/customer.constants';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const FILTERS_NAMES = {
  roles: 'Role',
  txCenters: 'TX Center Name',
};

export const TX_USERS_INVITED_TABLE_COLUMNS: TableColumnType[] = [
  col('contactName', 'TX User Name', {
    sortable: true,
    minWidthClassName: 'min-w-[240px]',
  }),
  col('email', 'E-mail', {
    sortable: true,
    minWidthClassName: 'min-w-[260px]',
  }),
  col('roles', 'Role', {
    sortable: false,
    minWidthClassName: 'min-w-[260px]',
  }),
  col('txCenterName', 'TX Center Name', {
    sortable: false,
    minWidthClassName: 'min-w-[260px]',
  }),
  col('invitedAt', 'Invited Date', {
    sortable: true,
    minWidthClassName: 'min-w-[135px]',
  }),
  col('primaryPhone', 'Primary Phone', {
    sortable: false,
    minWidthClassName: 'min-w-[260px]',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getActionsMenu = (onResendInvite: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'resend-tx-user-invite',
    type: 'action',
    to: 'resend-tx-user-invite',
    label: (
      <div className="flex items-center gap-2">
        <RiRepeatLine size={16} />
        <span className="text-m-r">Resend Invite</span>
      </div>
    ),
    onAction: () => onResendInvite(),
  },
  {
    key: 'delete-tx-center',
    type: 'action',
    to: 'delete-tx-center',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

export const getRenderTXUsersInvitedTableCell =
  (onResendInvite: (customer: CustomerMT) => void, onDelete: (customerId: string) => void) =>
  (customer: CustomerMT, columnKey: Key): ReactNode => {
    const { id, email, customerInTxCenters, invitedAt, primaryPhone, photoPath, firstName, lastName, txCentersList } =
      customer;

    const onTXUserResendInvite = () => onResendInvite(customer);
    const onTXCenterDelete = () => onDelete(id as string);
    const ADMIN_CAPABILITY_NAMES: Record<string, string> = {
      ...CUSTOMER_ROLE_ADMIN_NAMES,
      ...CUSTOMER_PERMISSION_NAMES,
    };

    const roles = customerInTxCenters
      ?.filter((item) => item && item.roles.length)
      .reduce((acc: string[], item) => {
        item.roles.forEach((role) => {
          const roleName = ADMIN_CAPABILITY_NAMES[role];
          if (!acc.includes(roleName)) acc.push(roleName);
        });
        return acc;
      }, [])
      .sort((a, b) => a.localeCompare(b));

    const sortedTxCentersList = txCentersList?.sort((a, b) => a.name.localeCompare(b.name));
    const ACTIONS_MENU = getActionsMenu(onTXUserResendInvite, onTXCenterDelete);

    switch (columnKey) {
      case 'contactName':
        return (
          <Link to={`/admin/customers/tx_users/${id}`} className="flex items-center gap-2">
            <TmdxAvatar
              className="shrink-0"
              radius="xl"
              size="xl"
              name={getFullName(firstName, lastName)}
              src={photoPath}
            />
            <span className="text-m-m">{getFullName(firstName, lastName)}</span>
          </Link>
        );
      case 'email':
        return <span className="text-m-r">{email}</span>;
      case 'roles':
        return (
          <div className="flex items-center gap-2 truncate">
            <ConditionalRender show={Boolean(roles?.length)} placeholder="-">
              {roles?.map((role, index) => {
                if (!index) {
                  return (
                    <Chip key={role} size="sm">
                      {role}
                    </Chip>
                  );
                }

                if (index === 1) {
                  const slicedItems = roles.slice(index);
                  const rolesName = slicedItems.map((customerRole) => customerRole).join(', ');

                  return (
                    <Tooltip placement="top" className="max-w-64" content={rolesName} key="additionalRolesCount">
                      <Chip>{`+${roles.length - index}`}</Chip>
                    </Tooltip>
                  );
                }

                return null;
              })}
            </ConditionalRender>
          </div>
        );
      case 'txCenterName':
        return (
          <div className="flex items-center gap-2 truncate">
            <ConditionalRender show={Boolean(customerInTxCenters?.length)} placeholder="-">
              {sortedTxCentersList?.map((item, index) => {
                if (!index) {
                  return <Chip key={item.id}>{item.name}</Chip>;
                }

                if (index === 1) {
                  const slicedItems = sortedTxCentersList.slice(index);
                  const txCenterNames = slicedItems.map((txCenterName) => txCenterName.name).join(', ');

                  return (
                    <Tooltip placement="top" className="max-w-64" content={txCenterNames} key="txCentersCount">
                      <Chip>{`+${sortedTxCentersList.length - index}`}</Chip>
                    </Tooltip>
                  );
                }

                return null;
              })}
            </ConditionalRender>
          </div>
        );
      case 'invitedAt':
        return <span className="text-m-r">{invitedAt ? format(invitedAt, 'LLL-dd-yyyy') : '-'}</span>;
      case 'primaryPhone':
        return <span className="text-m-r uppercase">{primaryPhone}</span>;
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label="TX User actions menu"
          />
        );
      default:
        return null;
    }
  };
